import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * Merges every static mesh under `root` that shares a material (and shadow flags)
 * within a spatial chunk into a single mesh. The procedural world is built from
 * thousands of primitives; this collapses them into a few hundred draw calls while
 * keeping frustum culling useful per chunk.
 *
 * Meshes flagged with `userData.noBatch` (animated or swappable things) are left alone.
 */
export function batchStatic(root: THREE.Object3D, chunkSize = 48): { before: number; after: number } {
  root.updateMatrixWorld(true);
  const groups = new Map<
    string,
    { material: THREE.Material; cast: boolean; receive: boolean; meshes: THREE.Mesh[] }
  >();
  let before = 0;

  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || m.userData.noBatch || Array.isArray(m.material)) return;
    if ((m as unknown as THREE.InstancedMesh).isInstancedMesh) return;
    let p: THREE.Object3D | null = m;
    while (p) {
      if (p.userData.noBatch) return;
      p = p.parent;
    }
    before++;
    const pos = new THREE.Vector3().setFromMatrixPosition(m.matrixWorld);
    const cx = Math.floor(pos.x / chunkSize);
    const cz = Math.floor(pos.z / chunkSize);
    const key = `${m.material.uuid}|${m.castShadow ? 1 : 0}${m.receiveShadow ? 1 : 0}|${cx},${cz}`;
    let g = groups.get(key);
    if (!g) {
      g = { material: m.material, cast: m.castShadow, receive: m.receiveShadow, meshes: [] };
      groups.set(key, g);
    }
    g.meshes.push(m);
  });

  let after = 0;
  for (const g of groups.values()) {
    if (g.meshes.length < 2) {
      after++;
      continue;
    }
    const geos: THREE.BufferGeometry[] = [];
    for (const m of g.meshes) {
      let geo = m.geometry.clone();
      geo.applyMatrix4(m.matrixWorld);
      if (geo.index) geo = geo.toNonIndexed();
      for (const name of Object.keys(geo.attributes)) {
        if (name !== 'position' && name !== 'normal' && name !== 'uv') geo.deleteAttribute(name);
      }
      if (!geo.attributes.normal) geo.computeVertexNormals();
      if (!geo.attributes.uv) {
        geo.setAttribute(
          'uv',
          new THREE.Float32BufferAttribute(new Float32Array(geo.attributes.position!.count * 2), 2),
        );
      }
      geo.clearGroups();
      geos.push(geo);
    }
    const merged = mergeGeometries(geos, false);
    for (const geo of geos) geo.dispose();
    if (!merged) {
      after += g.meshes.length;
      continue;
    }
    const mesh = new THREE.Mesh(merged, g.material);
    mesh.castShadow = g.cast;
    mesh.receiveShadow = g.receive;
    mesh.matrixAutoUpdate = false;
    root.add(mesh);
    for (const m of g.meshes) {
      m.removeFromParent();
      m.geometry.dispose();
    }
    after++;
  }

  // Drop groups left empty by merging.
  const empties: THREE.Object3D[] = [];
  root.traverse((o) => {
    if (o !== root && !(o as THREE.Mesh).isMesh && o.children.length === 0 && !o.userData.noBatch)
      empties.push(o);
  });
  for (const e of empties) e.removeFromParent();
  return { before, after };
}
